/**
 * Reports dashboard section: tabbed list with create/edit collapse.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { ReportsDataTable } from '@/components/reports/remote/ReportsDataTable';
import { EditReportView } from '@/components/reports/remote/EditReportView';
import { CreateReportView } from '@/components/reports/remote/CreateReportView';
import { SelectReportTypeModal } from '@/components/reports/remote/SelectReportTypeModal';
import { Select } from '@/components/forms/Select';
import { Button } from '@/components/forms/Button';
import { usePageActions } from '@/hooks/usePageActions';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { reportKeys } from '@/hooks/useReportsRemote';
import type { ReportTab } from '@/types/remote/report.types';
import {
  REMOTE_REPORT_TAB_LABELS as TAB_LABELS,
  REMOTE_REPORT_TAB_SESSION_TYPES as TAB_SESSION_TYPES,
  REMOTE_REPORT_TAB_COLORS as TAB_COLORS,
  SESSION_REMOTE_TYPE_CONFIG as SESSION_TYPE_CONFIG,
} from '@/components/reports/report-configs';
import { toReportTab } from '@/components/reports/remote/report-table-utils';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link ReportsSection}. */
interface ReportsSectionProps {
  layoutWidth?: ElementWidth;
}

/** Which form is mounted in the collapse wrapper. */
type ActiveForm = 'edit' | 'create' | null;

/** Report type tabs shown in the remote dashboard widget. */
const TAB_ORDER: ReportTab[] = [
  'tutoring',
  'information-meeting',
  'welcome-meeting',
  'field-event',
];

/** Dashboard widget: remote reports list with type picker. */
export const ReportsSection: React.FC<ReportsSectionProps> = ({ layoutWidth = 'full' }) => {
  const {
    canCreate,
    canEditRowTutoring,
    canEditRowInformationMeeting,
    canEditRowWelcome,
    canEditRowFieldEvent,
    canValidateTutoring,
    canValidateInformationMeeting,
    canValidateWelcome,
    canValidateFieldEvent,
    creatableTypes: creatableTypesRaw,
  } = usePageActions('reports');
  const { elements } = useInterfaceElements('reports');
  const queryClient = useQueryClient();
  const { openRowId, openRow, closeRow } = useDetailsView();
  const { viewMode, setViewMode: handleViewModeChange } = useViewMode('reports', 'list');

  const allowedTabIds = React.useMemo((): ReportTab[] => {
    const tabElements = elements.filter((el) => el.elementType === 'tab');
    const otherTabs: ReportTab[] =
      tabElements.length === 0
        ? TAB_ORDER
        : tabElements
            .map((el) => toReportTab(el.elementId))
            .filter((id): id is ReportTab => id !== null && id !== 'all');
    const sorted = [...otherTabs].sort((a, b) => TAB_ORDER.indexOf(a) - TAB_ORDER.indexOf(b));
    return sorted;
  }, [elements]);

  const [activeTab, setActiveTab] = React.useState<ReportTab>('tutoring');
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editId, setEditId] = React.useState<string | null>(null);
  const [typeModalOpen, setTypeModalOpen] = React.useState(false);
  const [selectedReportType, setSelectedReportType] = React.useState<ReportTab | null>(null);

  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditId(null);
    setSelectedReportType(null);
  }, []);

  /**
   * Types creatable from action-create-* RBAC elements (per-type).
   * Falls back to allowedTabIds when no action-create-* elements exist.
   */
  const creatableTabIds = React.useMemo((): ReportTab[] => {
    const TAB_IDS = new Set<ReportTab>([
      'tutoring',
      'information-meeting',
      'welcome-meeting',
      'field-event',
    ]);
    if (creatableTypesRaw.length > 0) {
      return creatableTypesRaw.filter((id): id is ReportTab => TAB_IDS.has(id as ReportTab));
    }
    return allowedTabIds.filter((id) => id !== 'all') as ReportTab[];
  }, [creatableTypesRaw, allowedTabIds]);

  const handleCreateClick = React.useCallback(() => {
    if (creatableTabIds.length === 1) {
      setSelectedReportType(creatableTabIds[0]);
      setActiveForm('create');
      setTypeModalOpen(false);
      closeRow();
    } else {
      setTypeModalOpen(true);
      setEditId(null);
      closeRow();
    }
  }, [creatableTabIds, closeRow]);

  const handleTypeSelect = (type: ReportTab) => {
    setSelectedReportType(type);
    setActiveForm('create');
    setTypeModalOpen(false);
    closeRow();
  };

  const handleEditReport = React.useCallback(
    (reportId: string) => {
      setEditId(reportId);
      setActiveForm('edit');
      closeRow();
    },
    [closeRow],
  );

  const handleFormSuccess = React.useCallback(() => {
    closeForm();
    void queryClient.invalidateQueries({ queryKey: reportKeys.all });
  }, [closeForm, queryClient]);

  const formContent = React.useMemo(() => {
    if (activeForm === 'edit' && editId) {
      return (
        <EditReportView
          key={editId}
          reportId={editId}
          onClose={closeForm}
          onSuccess={handleFormSuccess}
        />
      );
    }
    if (activeForm === 'create' && selectedReportType) {
      return (
        <CreateReportView
          sessionType={selectedReportType}
          onClose={closeForm}
          onSuccess={handleFormSuccess}
        />
      );
    }
    return null;
  }, [activeForm, editId, selectedReportType, closeForm, handleFormSuccess]);

  const tabItems = React.useMemo(
    () =>
      allowedTabIds.map((id) => {
        const sessionTypesForTab = TAB_SESSION_TYPES[id];
        const firstType = sessionTypesForTab?.[0];
        const Icon = firstType ? SESSION_TYPE_CONFIG[firstType]?.icon : undefined;
        return {
          id,
          label: TAB_LABELS[id],
          icon: Icon ? <Icon className="h-4 w-4" /> : undefined,
          color: TAB_COLORS[id],
        };
      }),
    [allowedTabIds],
  );

  const handleTabChange = React.useCallback(
    (id: string) => {
      closeForm();
      closeRow();
      setActiveTab(id as ReportTab);
    },
    [closeForm, closeRow],
  );

  const toolbarLeftNode = React.useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Select
          value={activeTab}
          onChange={handleTabChange}
          options={tabItems.map((tab) => ({
            value: tab.id,
            label: tab.label,
            icon: tab.icon,
            color: tab.color,
          }))}
          size="sm"
        />
        {canCreate && (
          <Button preset="create-entity" size="sm" onClick={handleCreateClick}>
            Créer
          </Button>
        )}
      </div>
    ),
    [tabItems, activeTab, canCreate, handleCreateClick, handleTabChange],
  );

  return (
    <CollapseReplaceWrapper
      mode="replace"
      isOpen={activeForm !== null}
      collapsePosition="top"
      formContent={formContent}
    >
      <ReportsDataTable
        activeTab={activeTab}
        allowedTabIds={allowedTabIds}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        onEditReport={handleEditReport}
        enableFilters={true}
        isSection={true}
        layoutWidth={layoutWidth}
        pageActions={{
          canEditRowTutoring,
          canEditRowInformationMeeting,
          canEditRowWelcome,
          canEditRowFieldEvent,
          canValidateTutoring,
          canValidateInformationMeeting,
          canValidateWelcome,
          canValidateFieldEvent,
        }}
        toolbarLeft={toolbarLeftNode}
      />

      <SelectReportTypeModal
        open={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        onSelect={handleTypeSelect}
        allowedTabIds={creatableTabIds}
      />
    </CollapseReplaceWrapper>
  );
};
